// Single-key shortcuts on or off (docs/CONTRACTS.md section 20.8), kept on this device in localStorage `nw:single-keys`.
// The shell's key code (lib/keys.ts, app/keys.ts) is frozen in P2 and every single-key handler asks `singleKeyAllowed`,
// which refuses an event already default-prevented. So while the choice is off, one capture listener on the window
// prevents the default of a printable single key outside text fields and dialogs, and no handler fires. Enter, Tab,
// Escape and the arrows are not single-key shortcuts and stay as they are; ⌘K and Ctrl+K carry a modifier.
import { useSyncExternalStore } from 'react'
import { typing } from '../../lib/keys.ts'

const KEY = 'nw:single-keys'

function stored(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'off'
  } catch {
    return true
  }
}

let on = stored()
const listeners = new Set<() => void>()

export function setSingleKeys(next: boolean): void {
  on = next
  try {
    localStorage.setItem(KEY, next ? 'on' : 'off')
  } catch {
    /* storage unavailable: the choice lasts for this page */
  }
  listeners.forEach((l) => l())
}

export function useSingleKeys(): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => on,
  )
  return [value, setSingleKeys]
}

let installed = false

/** Called once from the feature's entry file, so the choice holds on every screen. */
export function installSingleKeyGate(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener(
    'keydown',
    (e) => {
      if (on || e.key.length !== 1 || e.key === ' ' || e.metaKey || e.ctrlKey || e.altKey || typing(e)) return
      if (document.querySelector('[aria-modal="true"], [cmdk-dialog]')) return
      e.preventDefault()
    },
    true,
  )
}
